// Manuels Übersicht (E3, S2; Gernots Entscheidung 3: „Top-Qualität, kein
// Schein“) – die reine Rechnung zur Seite, ohne React: wie die Wochen und
// Monate heißen, wie hoch die Balken sind, welche Striche die Achse trägt,
// und wie Durchlauf, Wartezeit und Zähler in Worten lauten.
//
// Die Zahlen selbst zählt der Server (server/src/crm/uebersicht.ts); hier
// wird nichts dazugerechnet, geschätzt oder geglättet. Was 0 ist, zeigt 0.
// Tage kommen als Text „JJJJ-MM-TT“ (der Montag einer Woche, Wiener
// Kalender) und werden über UTC gerechnet, damit keine Zeitzone einen Tag
// verschiebt.

import type { Durchlauf, JahresZaehler } from "@happy-energy/kern";

/** Monate, wie man sie in Österreich schreibt – fest, damit kein Browser „Januar“ sagt. */
const MONATE = ["Jänner", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"] as const;
const MONATE_KURZ = ["Jän.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."] as const;

const TAG_MS = 86_400_000;

/** „JJJJ-MM-TT“ als UTC-Mitternacht. */
function alsUtc(tag: string): Date {
  const [j, m, t] = tag.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(j, m - 1, t));
}

// --- Wochen und Monate -----------------------------------------------------------------------

/** Die Kalenderwoche nach ISO 8601 (wie der österreichische Kalender): Woche 1 hat den ersten Donnerstag. */
export function kalenderwoche(tag: string): number {
  const d = alsUtc(tag);
  // Der Donnerstag dieser Woche entscheidet über das Jahr der Woche.
  const wochentag = (d.getUTCDay() + 6) % 7; // Montag 0 … Sonntag 6
  const donnerstag = new Date(d.getTime() + (3 - wochentag) * TAG_MS);
  const jahresanfang = Date.UTC(donnerstag.getUTCFullYear(), 0, 1);
  return 1 + Math.floor((donnerstag.getTime() - jahresanfang) / TAG_MS / 7);
}

/** „20.–26. Okt.“; über den Monatswechsel „27. Okt.–2. Nov.“ */
export function wochenSpanne(wocheAb: string): string {
  const montag = alsUtc(wocheAb);
  const sonntag = new Date(montag.getTime() + 6 * TAG_MS);
  const monat = (d: Date) => MONATE_KURZ[d.getUTCMonth()]!;
  if (montag.getUTCMonth() === sonntag.getUTCMonth()) return `${montag.getUTCDate()}.–${sonntag.getUTCDate()}. ${monat(sonntag)}`;
  return `${montag.getUTCDate()}. ${monat(montag)}–${sonntag.getUTCDate()}. ${monat(sonntag)}`;
}

/**
 * Eine Woche: „KW 43“, die Tage („19.–25. Okt.“ – für den Hinweis beim
 * Darüberfahren) und kurz fürs Diagramm „ab 19.10.“ (passt unter jeden
 * Balken, auch über den Monatswechsel).
 */
export function wocheBeschriften(wocheAb: string): { kurz: string; tage: string; ab: string } {
  const montag = alsUtc(wocheAb);
  return { kurz: `KW ${kalenderwoche(wocheAb)}`, tage: wochenSpanne(wocheAb), ab: `ab ${montag.getUTCDate()}.${montag.getUTCMonth() + 1}.` };
}

/** „2026-10“ → „Oktober 2026“ */
export function monatBeschriften(monat: string): string {
  const [j, m] = monat.split("-").map(Number) as [number, number];
  return `${MONATE[m - 1] ?? monat} ${j}`;
}

// --- Die Achse und die Balken ---------------------------------------------------------------

export interface Skala {
  /** Der obere Rand der Achse – ein runder Wert, mindestens 1. */
  hoechst: number;
  /** Die Striche der Achse, von 0 bis `hoechst`. */
  striche: number[];
}

/**
 * Eine Achse mit runden Strichen (1, 2, 5, 10, 20, 50 … je Schritt), höchstens
 * fünf, deren oberster den größten Wert gerade umfasst: 3 → 0–3, 7 → 0–8 in
 * Zweiern, 13 → 0–15 in Fünfern. Sind alle Werte 0, steht nur die 0 da
 * (die Achse reicht bis 1, damit nichts durch 0 teilt) – kein erfundener
 * Maßstab für Daten, die es nicht gibt.
 */
export function skala(werte: readonly number[]): Skala {
  const groesster = Math.max(0, ...werte.filter((w) => Number.isFinite(w)));
  if (groesster <= 0) return { hoechst: 1, striche: [0] };
  const schritt = runderSchritt(groesster);
  const hoechst = Math.ceil(groesster / schritt) * schritt;
  const striche: number[] = [];
  for (let s = 0; s <= hoechst; s += schritt) striche.push(s);
  return { hoechst, striche };
}

/** Der kleinste runde Schritt (1, 2, 5, 10, 20, 50 …), mit dem höchstens vier Schritte den Wert umfassen. */
function runderSchritt(groesster: number): number {
  for (let zehner = 1; ; zehner *= 10) {
    const passend = [1, 2, 5].map((f) => f * zehner).find((s) => Math.ceil(groesster / s) <= 4);
    if (passend !== undefined) return passend;
  }
}

/** Wie viel Prozent der Höhe ein Wert einnimmt – 0 bis 100, nie mehr. */
export function hoeheProzent(wert: number, hoechst: number): number {
  if (!(hoechst > 0) || !(wert > 0)) return 0;
  return Math.min(100, (wert / hoechst) * 100);
}

// --- Durchlauf, Wartezeit, Zähler in Worten ----------------------------------------------------------

/** 5.5 → „5,5“; 12 → „12“ */
function zahl(z: number): string {
  return String(z).replace(".", ",");
}

/**
 * Der Durchlauf von „gesendet“ bis „freigegeben“: „im Mittel 5,5 Stunden“
 * und dahinter, woraus gerechnet ist – der Median über die in 90 Tagen
 * freigegebenen. Ab zwei Tagen dazu die Tage, damit man es nicht rechnen muss.
 */
export function durchlaufText(d: Durchlauf): { wert: string; woraus: string } {
  if (d.anzahl === 0 || d.medianStunden === null) {
    return { wert: "noch keine Zahl", woraus: "In den letzten 90 Tagen wurde noch keine Besichtigung freigegeben." };
  }
  const h = d.medianStunden;
  const stunden = h === 1 ? "1 Stunde" : `${zahl(h)} Stunden`;
  const tage = h >= 48 ? ` (etwa ${zahl(Math.round((h / 24) * 10) / 10)} Tage)` : "";
  return {
    wert: `im Mittel ${stunden}${tage}`,
    woraus: `Median, ${d.anzahl === 1 ? "1 Besichtigung" : `${d.anzahl} Besichtigungen`} in 90 Tagen`,
  };
}

/** Wie lange etwas schon wartet, kurz: „3 Tage“, „5 Std.“, „12 Min.“, „gerade erst“. Gerechnet zum Stand des Servers. */
export function wartezeit(seit: string, stand: string): string {
  const ms = Date.parse(stand) - Date.parse(seit);
  if (!Number.isFinite(ms) || ms < 60_000) return "gerade erst";
  const minuten = Math.floor(ms / 60_000);
  if (minuten < 60) return `${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `${stunden} Std.`;
  const tage = Math.floor(stunden / 24);
  return tage === 1 ? "1 Tag" : `${tage} Tage`;
}

/** Der Zähler mit Anteilen am Ziel – ohne Ziel keine Anteile (kein Schein). */
export function zaehlerAnteile(z: JahresZaehler): { verkauft: number; montiert: number } | null {
  if (z.ziel === null || z.ziel <= 0) return null;
  return { verkauft: hoeheProzent(z.verkauft, z.ziel), montiert: hoeheProzent(z.montiert, z.ziel) };
}

/**
 * Eine Zahl, wie die Leitung sie als Ziel eintippt: ganze Anlagen, 0 bis
 * 100 000 (wie der Vertrag im Kern, jahreszielEingabe) – auch mit
 * Tausenderpunkt („1.200“). Halbe Anlagen oder Unsinn: null.
 */
export function zielLesen(text: string): number | null {
  const roh = text.trim().replace(/\s/g, "");
  if (!/^\d{1,6}$/.test(roh) && !/^\d{1,3}(\.\d{3})+$/.test(roh)) return null;
  const n = Number(roh.replace(/\./g, ""));
  return n <= 100_000 ? n : null;
}

// --- Der Montagetermin auf der Karte im Weg (seit dem Augentest, 21.10.2026) --------------------

const WOCHENTAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"] as const;

/** „Mo 10. Nov.“ – kurz für die kleine Karte, ohne Jahr. */
export function tagKurz(tag: string): string {
  const d = alsUtc(tag);
  if (Number.isNaN(d.getTime())) return tag;
  return `${WOCHENTAGE[d.getUTCDay()]} ${d.getUTCDate()}. ${MONATE_KURZ[d.getUTCMonth()]}`;
}

export interface TerminAufKarte {
  /** offen: noch kein Termin · geplant: kommt noch · laeuft: ab dem Tag, noch nicht als montiert eingetragen · montiert */
  art: "offen" | "geplant" | "laeuft" | "montiert";
  zeile: string;
  /** Was dazu eingetragen ist („Partie 2, 3 Tage“) – oder null. */
  text: string | null;
}

/**
 * Was die Karte bei Bestellung (bis E3c: Lager) und Montage über den Termin sagt. Gernot, 21.10.:
 * kein Rot nach dem Tag – eine Montage dauert ein bis zwei Tage, ehrlich
 * „überfällig“ sagen kann erst die Kiste Montage. Ab dem Tag deshalb neutral
 * „Montage seit …“, bis „montiert am“ in der Akte steht.
 */
export function terminAufKarte(k: { montagetermin: { am: string | null; text: string | null }; montiertAm: string | null }, heute: string): TerminAufKarte {
  const text = k.montagetermin.text?.trim() || null;
  if (k.montiertAm) return { art: "montiert", zeile: `montiert am ${tagKurz(k.montiertAm)}`, text: null };
  const am = k.montagetermin.am;
  if (!am) return { art: "offen", zeile: "Montagetermin noch offen", text };
  if (heute < am) return { art: "geplant", zeile: `Montage ab ${tagKurz(am)}`, text };
  return { art: "laeuft", zeile: `Montage seit ${tagKurz(am)}`, text };
}
